import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Star, Filter, Search, Navigation, Calendar } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
// Regional rating data per competitor
const regionalRatings = [{
  region: 'Nord',
  EDEKA: 4.2,
  REWE: 3.9,
  LIDL: 3.7,
  'ALDI Nord': 3.6,
  'ALDI Süd': 0,
  markets: 12
}, {
  region: 'Minden-Hannover',
  EDEKA: 3.9,
  REWE: 4.0,
  LIDL: 3.8,
  'ALDI Nord': 3.5,
  'ALDI Süd': 0,
  markets: 9
}, {
  region: 'Rhein-Ruhr',
  EDEKA: 3.8,
  REWE: 3.7,
  LIDL: 4.1,
  'ALDI Nord': 3.9,
  'ALDI Süd': 3.8,
  markets: 15
}, {
  region: 'Hessenring',
  EDEKA: 4.3,
  REWE: 4.1,
  LIDL: 3.6,
  'ALDI Nord': 3.4,
  'ALDI Süd': 3.5,
  markets: 8
}, {
  region: 'Nordbayern',
  EDEKA: 4.1,
  REWE: 3.8,
  LIDL: 3.9,
  'ALDI Nord': 0,
  'ALDI Süd': 3.7,
  markets: 11
}, {
  region: 'Südwest',
  EDEKA: 4.4,
  REWE: 4.2,
  LIDL: 3.8,
  'ALDI Nord': 0,
  'ALDI Süd': 3.6,
  markets: 10
}, {
  region: 'Südbayern',
  EDEKA: 4.5,
  REWE: 4.3,
  LIDL: 3.7,
  'ALDI Nord': 0,
  'ALDI Süd': 3.5,
  markets: 13
}];
// Radius-based mock data
const radiusData: Record<string, {
  name: string;
  type: string;
  rating: number;
  distance: string;
}[]> = {
  'Hamburg, Jungfernstieg': [{
    name: 'EDEKA Center HafenCity',
    type: 'EDEKA',
    rating: 4.3,
    distance: '0.6 km'
  }, {
    name: 'REWE City Jungfernstieg',
    type: 'REWE',
    rating: 3.9,
    distance: '0.2 km'
  }, {
    name: 'LIDL Hamburg-Mitte',
    type: 'LIDL',
    rating: 3.7,
    distance: '0.8 km'
  }, {
    name: 'ALDI Nord Neustadt',
    type: 'ALDI Nord',
    rating: 3.5,
    distance: '1.1 km'
  }, {
    name: 'EDEKA Marktplatz',
    type: 'EDEKA',
    rating: 4.1,
    distance: '1.4 km'
  }],
  'Berlin, Alexanderplatz': [{
    name: 'EDEKA Express Mitte',
    type: 'EDEKA',
    rating: 3.7,
    distance: '0.3 km'
  }, {
    name: 'REWE Berlin-Mitte',
    type: 'REWE',
    rating: 4.1,
    distance: '0.5 km'
  }, {
    name: 'LIDL Alexanderplatz',
    type: 'LIDL',
    rating: 4.0,
    distance: '0.7 km'
  }, {
    name: 'ALDI Nord Mitte',
    type: 'ALDI Nord',
    rating: 3.8,
    distance: '1.2 km'
  }],
  'München, Marienplatz': [{
    name: 'EDEKA München-Schwabing',
    type: 'EDEKA',
    rating: 4.6,
    distance: '0.9 km'
  }, {
    name: 'REWE Marienplatz',
    type: 'REWE',
    rating: 4.2,
    distance: '0.4 km'
  }, {
    name: 'LIDL München-Zentrum',
    type: 'LIDL',
    rating: 3.8,
    distance: '1.3 km'
  }, {
    name: 'ALDI Süd Zentrum',
    type: 'ALDI Süd',
    rating: 3.9,
    distance: '1.1 km'
  }, {
    name: 'EDEKA Hbf',
    type: 'EDEKA',
    rating: 4.0,
    distance: '1.8 km'
  }]
};
const regions = ['Alle Regionen', 'Nord', 'Minden-Hannover', 'Rhein-Ruhr', 'Hessenring', 'Nordbayern', 'Südwest', 'Südbayern'];
const radiusOptions = ['1 km', '2 km', '5 km', '10 km'];
const competitors = ['EDEKA', 'REWE', 'LIDL', 'ALDI Nord', 'ALDI Süd'];
const competitorBarColors: Record<string, string> = {
  EDEKA: '#fce530',
  REWE: '#cc061d',
  LIDL: '#0050AA',
  'ALDI Nord': '#007cc1',
  'ALDI Süd': '#00457c'
};
const competitorLogos: Record<string, string> = {
  EDEKA: 'https://www.edeka.de/edeka-ui-assets/images/logo.png',
  REWE: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/08/Rewe_Logo.png/250px-Rewe_Logo.png',
  LIDL: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/91/Lidl-Logo.svg/250px-Lidl-Logo.svg.png',
  'ALDI Nord': 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2c/Aldi_Nord_201x_logo.svg/250px-Aldi_Nord_201x_logo.svg.png',
  'ALDI Süd': 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8c/ALDI_Sued-Logo.svg/250px-ALDI_Sued-Logo.svg.png'
};
const timePeriods = [{
  id: '30',
  label: '30 Tage'
}, {
  id: '60',
  label: '60 Tage'
}, {
  id: '120',
  label: '120 Tage'
}, {
  id: '365',
  label: '365 Tage'
}, {
  id: 'gesamt',
  label: 'Gesamt'
}];
function applyTimeFactor(value: number, period: string, isRating: boolean = false): number {
  if (period === 'gesamt' || value === 0) return value;
  if (isRating) {
    const noise = period === '30' ? 0.1 : period === '60' ? -0.1 : period === '120' ? 0.05 : -0.05;
    return Math.max(1, Math.min(5, value + noise));
  }
  const factors: Record<string, number> = {
    '30': 0.08,
    '60': 0.17,
    '120': 0.33,
    '365': 1.0
  };
  return Math.max(1, Math.round(value * (factors[period] || 1)));
}
export function CompetitorRadarPage() {
  const [selectedRegion, setSelectedRegion] = useState('Alle Regionen');
  const [selectedPeriod, setSelectedPeriod] = useState('gesamt');
  const [addressInput, setAddressInput] = useState('');
  const [selectedRadius, setSelectedRadius] = useState('5 km');
  const [addressSearched, setAddressSearched] = useState('Hamburg, Jungfernstieg');
  const [hasSearched, setHasSearched] = useState(true);
  const rawFilteredRegionalData = selectedRegion === 'Alle Regionen' ? regionalRatings : regionalRatings.filter(r => r.region === selectedRegion);
  const filteredRegionalData = rawFilteredRegionalData.map(r => ({
    ...r,
    EDEKA: applyTimeFactor(r.EDEKA, selectedPeriod, true),
    REWE: applyTimeFactor(r.REWE, selectedPeriod, true),
    LIDL: applyTimeFactor(r.LIDL, selectedPeriod, true),
    'ALDI Nord': r['ALDI Nord'] > 0 ? applyTimeFactor(r['ALDI Nord'], selectedPeriod, true) : 0,
    'ALDI Süd': r['ALDI Süd'] > 0 ? applyTimeFactor(r['ALDI Süd'], selectedPeriod, true) : 0,
    markets: applyTimeFactor(r.markets, selectedPeriod)
  }));
  // When "Alle Regionen" is selected, aggregate to a single "Gesamt" bar
  const regionBarData = selectedRegion === 'Alle Regionen' ? (() => {
    const avg = (key: string) => {
      const vals = filteredRegionalData.map(r => (r as any)[key]).filter((v: number) => v > 0);
      return vals.length > 0 ? vals.reduce((a: number, b: number) => a + b, 0) / vals.length : null;
    };
    return [{
      region: 'Gesamt',
      EDEKA: avg('EDEKA'),
      REWE: avg('REWE'),
      LIDL: avg('LIDL'),
      'ALDI Nord': avg('ALDI Nord'),
      'ALDI Süd': avg('ALDI Süd')
    }];
  })() : filteredRegionalData.map(r => ({
    region: r.region,
    EDEKA: r.EDEKA,
    REWE: r.REWE,
    LIDL: r.LIDL,
    'ALDI Nord': r['ALDI Nord'] || null,
    'ALDI Süd': r['ALDI Süd'] || null
  }));
  // Custom bar label that renders a logo inside the bar
  const renderBarLogo = (comp: string) => (props: any) => {
    const {
      x,
      y,
      width,
      height,
      value
    } = props;
    if (!value || value === 0 || width < 10 || height < 20) return null;
    const logoSize = Math.min(width - 2, 22);
    return <image href={competitorLogos[comp]} x={x + (width - logoSize) / 2} y={y + 4} width={logoSize} height={logoSize} preserveAspectRatio="xMidYMid meet" data-id="element-331" />;
  };
  const rawRadiusStores = radiusData[addressSearched] || radiusData['Hamburg, Jungfernstieg'];
  const radiusStores = rawRadiusStores.map(s => ({
    ...s,
    rating: applyTimeFactor(s.rating, selectedPeriod, true)
  }));
  const radiusAggregated = competitors.map(type => {
    const stores = radiusStores.filter(s => s.type === type);
    const avg = stores.length > 0 ? stores.reduce((acc, s) => acc + s.rating, 0) / stores.length : null;
    return {
      name: type,
      rating: avg,
      count: stores.length
    };
  }).filter(d => d.rating !== null);
  const handleSearch = () => {
    const key = Object.keys(radiusData).find(k => k.toLowerCase().includes(addressInput.toLowerCase().split(',')[0].toLowerCase())) || 'Hamburg, Jungfernstieg';
    setAddressSearched(key);
    setHasSearched(true);
  };
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-332">
      {/* Regional Rating Comparison Card */}
      <Card data-id="element-333">
        <div className="p-6" data-id="element-334">
          <div className="flex items-start justify-between mb-5 flex-wrap gap-4" data-id="element-335">
            <div data-id="element-336">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-337">
                Regionaler Bewertungsvergleich
              </h3>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-338">
                Durchschnittliche Google-Bewertungen nach Region
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 mb-5" data-id="element-339">
            <div className="flex items-center gap-3 flex-wrap" data-id="element-340">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-600" data-id="element-341">
                <Filter size={14} data-id="element-342" />
                <span data-id="element-343">Region:</span>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-344">
                {regions.map(region => <button key={region} onClick={() => setSelectedRegion(region)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedRegion === region ? 'bg-edeka-blue text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-345">
                    {region}
                  </button>)}
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap" data-id="element-346">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-600" data-id="element-347">
                <Calendar size={14} data-id="element-348" />
                <span data-id="element-349">Zeitraum:</span>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-350">
                {timePeriods.map(period => <button key={period.id} onClick={() => setSelectedPeriod(period.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedPeriod === period.id ? 'bg-edeka-blue text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-351">
                    {period.label}
                  </button>)}
              </div>
            </div>
          </div>

          <div className="h-64" data-id="element-352">
            <ResponsiveContainer width="100%" height="100%" data-id="element-353">
              <BarChart data={regionBarData} margin={{
              top: 5,
              right: 20,
              left: 0,
              bottom: 5
            }} barCategoryGap={selectedRegion === 'Alle Regionen' ? '30%' : '25%'} barGap={selectedRegion === 'Alle Regionen' ? 6 : 3} data-id="element-354">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-355" />
                <XAxis dataKey="region" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#6b7280'
              }} data-id="element-356" />
                <YAxis domain={[3, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={v => v.toFixed(1) + ' ★'} data-id="element-357" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [value.toFixed(1) + ' ★', name]} data-id="element-358" />
                {competitors.map(comp => <Bar key={comp} dataKey={comp} name={comp} fill={competitorBarColors[comp]} radius={[3, 3, 0, 0]} fillOpacity={comp === 'EDEKA' ? 1 : 0.55} label={renderBarLogo(comp)} data-id="element-359" />)}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex items-center justify-center gap-8 mt-4 flex-wrap" data-id="element-360">
            {competitors.map(comp => <div key={comp} className="flex items-center gap-2" data-id="element-361">
                <img src={competitorLogos[comp]} alt={comp} className="h-6 w-auto object-contain" data-id="element-362" />
                <span className={`text-sm ${comp === 'EDEKA' ? 'font-bold text-gray-900' : 'font-medium text-gray-600'}`} data-id="element-363">
                  {comp}
                </span>
              </div>)}
          </div>
        </div>
      </Card>

      {/* Radius Search Card */}
      <Card data-id="element-364">
        <div className="p-6" data-id="element-365">
          <div className="flex items-start justify-between mb-5 flex-wrap gap-4" data-id="element-366">
            <div data-id="element-367">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-368">
                Lokale Umkreissuche
              </h3>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-369">
                Bewertungen im direkten Umfeld vergleichen
              </p>
            </div>
          </div>

          <div className="flex gap-3 mb-5 flex-wrap" data-id="element-370">
            <div className="flex-1 min-w-48 relative" data-id="element-371">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-372" />
              <input type="text" placeholder="Adresse eingeben, z.B. Hamburg, Jungfernstieg" value={addressInput} onChange={e => setAddressInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-edeka-blue/20 focus:border-edeka-blue" data-id="element-373" />
            </div>
            <div className="flex items-center gap-2" data-id="element-374">
              <span className="text-sm text-gray-500 whitespace-nowrap" data-id="element-375">
                Radius:
              </span>
              <div className="flex gap-1" data-id="element-376">
                {radiusOptions.map(r => <button key={r} onClick={() => setSelectedRadius(r)} className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${selectedRadius === r ? 'bg-edeka-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-377">
                    {r}
                  </button>)}
              </div>
            </div>
            <button onClick={handleSearch} className="flex items-center gap-2 px-4 py-2.5 bg-edeka-blue text-white rounded-lg text-sm font-medium hover:bg-edeka-blue/90 transition-colors" data-id="element-378">
              <Search size={14} data-id="element-379" />
              Suchen
            </button>
          </div>

          {hasSearched && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} data-id="element-380">
              <div className="text-xs text-gray-500 mb-4 flex items-center gap-1.5" data-id="element-381">
                <Navigation size={12} data-id="element-382" />
                <span data-id="element-383">
                  Ergebnisse für{' '}
                  <strong className="text-gray-700" data-id="element-384">{addressSearched}</strong>{' '}
                  im Umkreis von{' '}
                  <strong className="text-gray-700" data-id="element-385">{selectedRadius}</strong> —{' '}
                  {radiusStores.length} Märkte gefunden
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-id="element-386">
                <div data-id="element-387">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-388">
                    Ø Bewertung nach Kette
                  </div>
                  <div className="space-y-3" data-id="element-389">
                    {radiusAggregated.sort((a, b) => (b.rating || 0) - (a.rating || 0)).map((item, i) => <div key={item.name} className="flex items-center gap-3" data-id="element-390">
                          <img src={competitorLogos[item.name]} alt={item.name} className="h-5 w-5 object-contain shrink-0" data-id="element-391" />
                          <div className="w-20 text-sm font-medium text-gray-700" data-id="element-392">
                            {item.name}
                          </div>
                          <div className="flex-1 h-7 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-393">
                            <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: `${(item.rating || 0) / 5 * 100}%`
                    }} transition={{
                      duration: 0.5,
                      delay: i * 0.08
                    }} className="h-full rounded-full" style={{
                      backgroundColor: competitorBarColors[item.name],
                      opacity: item.name === 'EDEKA' ? 1 : 0.6
                    }} data-id="element-394" />
                          </div>
                          <div className="flex items-center gap-1 w-16 text-right" data-id="element-395">
                            <span className={`text-sm font-bold ${item.name === 'EDEKA' ? 'text-edeka-blue' : 'text-gray-600'}`} data-id="element-396">
                              {item.rating?.toFixed(1)}
                            </span>
                            <Star size={11} className="text-yellow-400 fill-yellow-400" data-id="element-397" />
                          </div>
                          <div className="text-xs text-gray-400 w-14" data-id="element-398">
                            {item.count} Märkte
                          </div>
                        </div>)}
                  </div>
                </div>

                <div data-id="element-399">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-400">
                    Einzelne Märkte im Umkreis
                  </div>
                  <div className="space-y-2" data-id="element-401">
                    {radiusStores.map((store, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  x: 10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: i * 0.06
                }} className={`flex items-center justify-between p-2.5 rounded-lg border ${store.type === 'EDEKA' ? 'border-edeka-blue/20 bg-edeka-blue/5' : 'border-gray-100 bg-gray-50'}`} data-id="element-402">
                        <div className="flex items-center gap-2 min-w-0" data-id="element-403">
                          <img src={competitorLogos[store.type]} alt={store.type} className="h-4 w-4 object-contain shrink-0" data-id="element-404" />
                          <span className={`text-sm truncate ${store.type === 'EDEKA' ? 'font-medium text-gray-900' : 'text-gray-600'}`} data-id="element-405">
                            {store.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 ml-2" data-id="element-406">
                          <span className="text-xs text-gray-400" data-id="element-407">
                            {store.distance}
                          </span>
                          <div className="flex items-center gap-1" data-id="element-408">
                            <span className={`text-sm font-semibold ${store.type === 'EDEKA' ? 'text-edeka-blue' : 'text-gray-600'}`} data-id="element-409">
                              {store.rating.toFixed(1)}
                            </span>
                            <Star size={11} className="text-yellow-400 fill-yellow-400" data-id="element-410" />
                          </div>
                        </div>
                      </motion.div>)}
                  </div>
                </div>
              </div>
            </motion.div>}
        </div>
      </Card>
    </div>;
}